import React from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import * as Popover from "@radix-ui/react-popover";

import {
  alertEntity,
  assetEntity,
  deviceEntity,
  readerEntity,
  SearchableEntity,
  tripEntity,
} from "@/components/common/Search/types";
import { Check20, ExpandLess20, ExpandMore20 } from "@/components/ui/SvgIcon";

import cn from "@/utils/cn";
type Props = React.HTMLAttributes<HTMLDivElement> & {
  entity: SearchableEntity;
  setEntity: React.Dispatch<React.SetStateAction<SearchableEntity>>;
};
export const SearchEntitySelector = ({
  entity,
  setEntity,
  className = "",
}: Props) => {
  const intl = useIntl();
  const [open, setOpen] = React.useState(false);
  function onSelect(value: SearchableEntity) {
    setEntity(value);
    setOpen(false);
  }

  const location = useLocation();
  React.useEffect(() => {
    switch (true) {
      case /trips/.test(location.pathname):
        setEntity(tripEntity);
        break;
      case /assets/.test(location.pathname):
        setEntity(assetEntity);
        break;
      case /alerts/.test(location.pathname):
        setEntity(alertEntity);
        break;
      case /system\/readers/.test(location.pathname):
        setEntity(readerEntity);
        break;
      case /system/.test(location.pathname):
        setEntity(deviceEntity);
        break;
    }
  }, [location, setEntity]);

  React.useEffect(() => {
    const handleScroll = () => {
      setOpen(false);
    };

    if (open) {
      document.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [open]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          data-testid="search-entity-selector"
          className={cn(
            "relative z-[110] -ml-px flex h-12 w-[100px] items-center justify-end gap-0.5 rounded-r-full border-l border-white bg-chorus-gray-100 pl-2 pr-3 aria-[expanded=true]:bg-chorus-teal-200 md:border-chorus-gray-100 md:bg-white md:aria-[expanded=true]:bg-chorus-gray-100",
            className,
          )}
        >
          {intl.formatMessage(entity.label)}
          {open ? (
            <ExpandLess20 className="fill-chorus-gray-900" />
          ) : (
            <ExpandMore20 className="fill-chorus-gray-900" />
          )}
        </button>
      </Popover.Trigger>

      <Popover.Content
        data-popover-content
        className="z-[200] mt-3 w-[136px] rounded-lg border border-chorus-gray-100 bg-white px-[6px] py-[9px] text-sm font-medium shadow-box-4"
        data-testid="search-entity-selector-dropdown"
        align={"end"}
      >
        {[tripEntity, assetEntity, deviceEntity, alertEntity, readerEntity].map(
          (value: SearchableEntity) => (
            <div
              key={value.key}
              onClick={() => onSelect(value)}
              className="relative cursor-pointer rounded-md p-2.5 pt-2 hover:bg-chorus-gray-50"
            >
              {intl.formatMessage(value.label)}
              {entity === value && (
                <Check20 className="absolute right-2 top-2" />
              )}
            </div>
          ),
        )}
      </Popover.Content>
    </Popover.Root>
  );
};
